import React, { useEffect, useState } from "react";
import { X, Gift } from "lucide-react";
import axios from "axios";
import { useToast } from "../hooks/use-toast";

const STORAGE_KEY = "mm_promo_seen";
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function FirstVisitPromo() {
  const [open, setOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    if (localStorage.getItem(STORAGE_KEY)) return;
    const t = setTimeout(() => setOpen(true), 5000);
    return () => clearTimeout(t);
  }, []);

  const close = () => {
    localStorage.setItem(STORAGE_KEY, "1");
    setOpen(false);
  };

  const claim = async (e) => {
    e.preventDefault();
    if (!/^\S+@\S+\.\S+$/.test(email.trim())) {
      toast({ title: "Invalid email", description: "Please enter a valid email." });
      return;
    }
    // Persist locally (nice-to-have offline redundancy)
    const list = JSON.parse(localStorage.getItem("mm_newsletter") || "[]");
    list.push({ email: email.trim(), source: "first_visit_promo", at: Date.now() });
    localStorage.setItem("mm_newsletter", JSON.stringify(list));
    // Fire-and-forget upsert to the backend so mama sees every lead in /admin.
    try {
      await axios.post(`${API}/leads`, { email: email.trim(), source: "first_visit_popup" });
    } catch (err) {
      // Silent — customer already sees the coupon so we don't want to block the UX.
      console.warn("Lead capture failed", err?.response?.status || err?.message);
    }
    setSubmitted(true);
    localStorage.setItem(STORAGE_KEY, "1");
  };

  return (
    <>
      <div
        onClick={close}
        className={`fixed inset-0 z-[85] bg-black/70 backdrop-blur-sm transition-opacity duration-300 ${
          open ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      />
      <div
        className={`fixed left-1/2 top-1/2 z-[95] w-full max-w-md -translate-x-1/2 -translate-y-1/2 px-6 transition-all duration-500 ${
          open ? "opacity-100 scale-100" : "opacity-0 scale-95 pointer-events-none"
        }`}
      >
        <div className="bg-[var(--mm-bg)] border border-[var(--mm-accent)] p-8 relative overflow-hidden">
          <button onClick={close} aria-label="Close" className="absolute top-4 right-4 text-[var(--mm-cream)] hover:text-[var(--mm-accent)] transition-colors">
            <X size={18} />
          </button>

          {!submitted ? (
            <>
              <div className="w-14 h-14 rounded-full border border-[var(--mm-accent)] flex items-center justify-center mb-6">
                <Gift size={22} className="text-[var(--mm-accent)]" />
              </div>
              <div className="section-label mb-3">Welcome to Mamamina's</div>
              <h2 className="font-display italic text-4xl text-[var(--mm-cream)] leading-tight mb-3">
                10% off your <span className="text-[var(--mm-accent)]">first feast.</span>
              </h2>
              <p className="text-[var(--mm-cream-muted)] font-light mb-6 text-sm leading-relaxed">
                Drop your email and we'll send you the code. First-time customers only.
              </p>
              <form onSubmit={claim} noValidate className="space-y-3">
                <input
                  data-testid="promo-email"
                  type="text"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@email.com"
                  className="w-full bg-transparent border border-[var(--mm-line)] text-[var(--mm-cream)] placeholder:text-[var(--mm-cream-muted)]/60 px-4 py-3 focus:border-[var(--mm-accent)] outline-none"
                />
                <button type="submit" className="btn-primary w-full text-center">Claim 10% Off</button>
              </form>
              <button onClick={close} className="w-full text-center mt-3 text-xs tracking-[0.22em] uppercase text-[var(--mm-cream-muted)] hover:text-[var(--mm-accent)] transition-colors">
                No thanks
              </button>
            </>
          ) : (
            <>
              <div className="section-label mb-3">Your code</div>
              <h2 className="font-display italic text-3xl text-[var(--mm-cream)] leading-tight mb-4">
                Welcome to the <span className="text-[var(--mm-accent)]">family.</span>
              </h2>
              <div className="font-mono tracking-[0.3em] text-3xl text-[var(--mm-accent)] bg-[var(--mm-bg-alt)] border border-[var(--mm-accent)] py-4 text-center mb-4">
                WELCOME10
              </div>
              <p className="text-[var(--mm-cream-muted)] font-light text-sm mb-6">
                Add this at checkout. Valid on your first order only.
              </p>
              <button onClick={close} className="btn-primary w-full text-center">Start Ordering</button>
            </>
          )}
        </div>
      </div>
    </>
  );
}
